import {
  OrderStatus,
  KatanaPerpsOrderEventData,
  ChainTransactionStatus,
  OrderType,
  calculateInitialMarginFractionWithOverride,
  decimalToPip,
  convertToLeverageParametersBigInt,
  pipToDecimal,
  LeverageParameters,
} from '@katanaperps/katana-perps-sdk';

import { EXCHANGE_DECIMALS } from 'config/constants';
import { BigNumberClass, BigNumberJs } from 'lib/BigNumber';
import { formatNumber } from 'lib/display/formatting';

import {
  ApiOrder,
  ApiOrderRaw,
  ApiFill,
  ApiOrderFillRaw,
  ApiPosition,
  ApiWallet,
} from './types';

export const readableFillStatus = (txStatus: ChainTransactionStatus) => {
  if (txStatus === ChainTransactionStatus.mined) {
    return 'complete';
  }
  return txStatus;
};

export const includeBaseAndQuoteFromMarket = (
  order: ApiOrderRaw | ApiOrder
): ApiOrder => {
  const [baseSymbol, quoteSymbol] = order.market.split('-');
  // We just include props on object (mutate),
  // to make it super fast...
  (order as ApiOrder).baseSymbol = baseSymbol;
  (order as ApiOrder).quoteSymbol = quoteSymbol;
  return order as ApiOrder;
};

export const transformOrderFillToApiFill = (
  order: KatanaPerpsOrderEventData,
  fill: ApiOrderFillRaw
): ApiFill => {
  return {
    ...fill,
    orderId: !order.type ? undefined : order.orderId,
    side: order.side,
    market: order.market,
  };
};

export const isOrderInactive = (status: OrderStatus, orderType: OrderType) =>
  status === OrderStatus.canceled ||
  status === OrderStatus.filled ||
  status === OrderStatus.inactive ||
  status === OrderStatus.notFound ||
  // Market orders are closed after executed (fully, or partially)
  (status === OrderStatus.partiallyFilled &&
    (orderType === 'stopLossMarket' ||
      orderType === 'takeProfitMarket' ||
      orderType === 'trailingStopMarket' ||
      orderType === 'market'));

export const filterInactiveOrders = (orders: ApiOrderRaw[]) => {
  return orders.filter(order => {
    const isInactive = isOrderInactive(order.status, order.type);
    if (isInactive) {
      console.warn(
        'Orders list contain inactive order - ignoring order',
        order
      );
    }
    return !isInactive;
  });
};

export const isPositionShort = (position: Pick<ApiPosition, 'quantity'>) =>
  Number(position.quantity) < 0;

export const renderLiquidationPrice = (
  liquidationPrice: ApiPosition['liquidationPrice'] | undefined,
  tickSizeDecimals: number | undefined
) =>
  !liquidationPrice || Number(liquidationPrice) === 0
    ? '-'
    : formatNumber(liquidationPrice, {
        fixedDecimals: tickSizeDecimals ?? EXCHANGE_DECIMALS,
      });

/**
 * Unrealized P&L % change is calculated using Position Margin as the starting point.
 * E.g. user has $1,000 of collateral and opens a 20x long for a position value of $20,000. Position margin is $1,000.
 *  - Position increases by $1,000 to $21,000
 *  - Currently this shows an increase % of ~5% ($1,000/$20,000)
 *  - This should instead show an increase of (Unrealized P&L/Position Margin) = ($1,000/$1,000) = 100%
 */
export const calculateUnrealizedPnLPercent = (
  position:
    | (Omit<Pick<ApiPosition, 'entryPrice' | 'quantity'>, 'unrealizedPnL'> & {
        unrealizedPnL: string | number;
      })
    | undefined,
  marketLeverageParams: LeverageParameters | undefined,
  initialMarginFractionOverride: string | null
) => {
  if (!position || !marketLeverageParams) {
    return;
  }
  const unrealizedPnL = Number(position.unrealizedPnL);
  const entryPrice = Number(position.entryPrice);
  const quantity = Number(position.quantity);
  const IMF = pipToDecimal(
    calculateInitialMarginFractionWithOverride({
      baseQuantity: decimalToPip(position.quantity),
      initialMarginFractionOverride: initialMarginFractionOverride
        ? decimalToPip(initialMarginFractionOverride)
        : null,
      leverageParameters:
        convertToLeverageParametersBigInt(marketLeverageParams),
    })
  );
  /** Initial Margin Requirement */
  const IMR = Math.abs(quantity) * Number(IMF) * entryPrice;
  return (unrealizedPnL * 100) / IMR;

  // Old calculation (Based on non-IMR value)
  // return (unrealizedPnL * 100) / (entryPrice * Math.abs(quantity));
};

// export function getAllowedOnfidoReferralHostRegex(host?: string): string {
//   const currentHost = host || window?.location?.host;
//   // Domains
//   if (['localhost:3000', 'app.io'].some(domain => domain === currentHost)) {
//     return currentHost;
//   }
//   // Subdomains
//   const domainFound = ['kuma-dev.com', 'app.io'].find(domain =>
//     currentHost.endsWith(domain)
//   );
//   if (domainFound) {
//     return `*.${domainFound}`;
//   }
//   if (!IS_TEST_ENVIRONMENT) {
//     console.error('Not allowed Onfido host');
//   }
//   return '';
// }

export const calculateBuyingPower = (
  availableCollateral: string | undefined,
  marketInitialMarginFraction: string | undefined,
  userInitialMarginFractionOverride: string | undefined | null
) => {
  return availableCollateral && marketInitialMarginFraction
    ? Number(availableCollateral) /
        Number(
          getMarginRatio(
            marketInitialMarginFraction,
            userInitialMarginFractionOverride
          )
        )
    : undefined;
};

/** margin ratio = 1/Leverage, eg. 10x leverage = 0.1 ratio */
export const getMarginRatio = (
  marketInitialMarginFraction: string | undefined,
  userInitialMarginFractionOverride: string | undefined | null
): string | undefined => {
  if (userInitialMarginFractionOverride === undefined) {
    // Not initialized properly yet
    return undefined;
  }
  return marketInitialMarginFraction
    ? userInitialMarginFractionOverride || marketInitialMarginFraction
    : undefined;
};

/**
 * Use initialMarginFraction to calculate leverage.
 * Round leverage to whole numbers by default.
 *  @example Leverage = 1 / IMF
 */
export const calculateLeverage = (
  initialMarginFraction: string | number,
  autoRound = true
) => {
  const leverage = 1 / Number(initialMarginFraction);
  return autoRound ? Math.round(leverage) : leverage;
};

/**
 * Round down on 8 decimals.
 * Eg. 21x 0.04761904761904761905 to be 0.04761904 = 21,00000336 instead of 20.99...
 */
export const calculateInitialMarginFractionFromLeverageAndRoundUp = (
  leverage: string | number
) => {
  return BigNumberJs(1)
    .dividedBy(leverage)
    .toFixed(8, BigNumberClass.ROUND_DOWN);
};

export const calculateWalletDiscountPercentage = (
  walletWithPositionsData: ApiWallet | undefined,
  defaultMakerFeeRate: string | undefined,
  defaultTakerFeeRate: string | undefined
) => {
  return walletWithPositionsData && defaultTakerFeeRate && defaultMakerFeeRate
    ? Math.max(
        1 -
          Number(walletWithPositionsData.makerFeeRate) /
            Number(defaultMakerFeeRate),
        1 -
          Number(walletWithPositionsData.takerFeeRate) /
            Number(defaultTakerFeeRate)
      ) * 100
    : undefined;
};

// TODO: fees also here global override?
// export const calculateWalletDiscountPercentage = (
//   walletWithPositionsData: ApiWallet | undefined,
//   defaultMakerFeeRate: string | undefined,
//   defaultTakerFeeRate: string | undefined
// ) => {
//   // Compare against the hardcoded base fee override when defined (converted
//   // from percent to rate); the API currently returns already-discounted
//   // defaults. Falls back to the API default when no override is set.
//   const baseMakerRate =
//     BASE_MAKER_FEE_OVERRIDE_PERCENT != null
//       ? BASE_MAKER_FEE_OVERRIDE_PERCENT / 100
//       : defaultMakerFeeRate != null
//       ? Number(defaultMakerFeeRate)
//       : undefined;
//   const baseTakerRate =
//     BASE_TAKER_FEE_OVERRIDE_PERCENT != null
//       ? BASE_TAKER_FEE_OVERRIDE_PERCENT / 100
//       : defaultTakerFeeRate != null
//       ? Number(defaultTakerFeeRate)
//       : undefined;

//   return walletWithPositionsData && baseTakerRate && baseMakerRate
//     ? Math.max(
//         1 - Number(walletWithPositionsData.makerFeeRate) / baseMakerRate,
//         1 - Number(walletWithPositionsData.takerFeeRate) / baseTakerRate
//       ) * 100
//     : undefined;
// };
